import { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Grid, Paper, Chip, Alert, Collapse, IconButton, FormControlLabel, Switch, Tooltip } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import DateRangePicker from './DateRangePicker';
import { fetchAllEmailPerformance, fetchEmailPerformanceSummaryV2, fetchGroupEmailPerformance, EmailPerformanceData, DateRange, fetchEmailClickData, EmailClickData } from '../../services/analyticsService';
import { LoadingState } from '../../components/LoadingState';
import { format, eachDayOfInterval, parseISO } from 'date-fns';
import { useUser } from '../../contexts/UserContext';
import { apiService } from '../../services/apiService';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ResponsiveBar } from '@nivo/bar';
import EmailPreviewModal from '../EmailPreviewModal';
import ContactListModal from '../ContactListModal';
import GeographicMapModal from './GeographicMapModal';
import MetricCard from './MetricCard';

// Get color for segment
const getSegmentColor = (segment_name: string) => {
  switch (segment_name?.toLowerCase()) {
    case 'newsletter':
      return '#3b82f6'; // blue
    case 'luxury':
      return '#9333ea'; // purple
    case 'romance':
      return '#ec4899'; // pink
    case 'family':
      return '#22c55e'; // green
    case 'ultralux':
      return '#f59e0b'; // amber
    case 'unsubscribed':
      return '#9ca3af'; // light grey
    default:
      return '#3174ad'; // default blue
  }
};

interface Props {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  groupId?: number;
  clientId?: number | null;
  includeSpamChecker?: boolean;
  onSpamCheckerChange?: (includeSpam: boolean) => void;
}

export default function EmailPerformance({ dateRange, onDateRangeChange, groupId, clientId, includeSpamChecker = false, onSpamCheckerChange }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isFetchingRef = useRef(false);
  const [data, setData] = useState<EmailPerformanceData[]>([]);
  const [expandedEmails, setExpandedEmails] = useState<Set<number>>(new Set());
  const [clickDataMap, setClickDataMap] = useState<Map<number, EmailClickData>>(new Map());
  
  // Contact list modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedEmailForModal, setSelectedEmailForModal] = useState<{id: number, name: string} | null>(null);
  const [selectedMetricForModal, setSelectedMetricForModal] = useState<'sent' | 'opens' | 'clicks' | 'unique_clicks'>('sent');
  
  const [loadingClickData, setLoadingClickData] = useState<Set<number>>(new Set());
  const [showSends, setShowSends] = useState(true);
  const [showOpens, setShowOpens] = useState(true);
  const [showClicks, setShowClicks] = useState(true);
  const { selectedClientId } = useUser();
  
  // Email preview modal state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewEmail, setPreviewEmail] = useState<EmailPerformanceData | null>(null);
  
  // Map modal state
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedEmailForMap, setSelectedEmailForMap] = useState<{id: number, name: string} | null>(null);
  const [selectedMetricForMap, setSelectedMetricForMap] = useState<'opens' | 'clicks'>('opens');
  const [mapData, setMapData] = useState<any[]>([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  
  // Use provided clientId if available, otherwise fall back to selectedClientId
  const effectiveClientId = clientId || selectedClientId;
  
  // Check if this is group analytics mode
  const isGroupMode = !!groupId;

  useEffect(() => {
    const fetchData = async () => {
      if (!effectiveClientId && !groupId || !dateRange.startDate || !dateRange.endDate) return;

      // Prevent duplicate calls in React StrictMode
      if (isFetchingRef.current) return;

      try {
        isFetchingRef.current = true;
        setLoading(true);
        setError(null);
        
        // Group analytics mode
        if (isGroupMode && groupId) {
          console.log('Fetching group email performance data:', { groupId });
          
          const transformedData = await fetchGroupEmailPerformance(
            groupId,
            dateRange.startDate.toISOString().split('T')[0],
            dateRange.endDate.toISOString().split('T')[0],
            includeSpamChecker
          );
          
          setData(transformedData);
          return;
        }
        
        // Client analytics mode (existing logic)
        const summaryData = await fetchEmailPerformanceSummaryV2(
          effectiveClientId!,
          dateRange.startDate.toISOString().split('T')[0],
          dateRange.endDate.toISOString().split('T')[0],
          !includeSpamChecker // When "Filter out spam" is OFF, show all data (include_spam=true)
        );
        
        // Transform V2 data to match existing EmailPerformanceData interface
        const transformedData: EmailPerformanceData[] = summaryData.emails.map(email => ({
          email_id: email.email_id,
          email_name: email.email_name,
          subject: email.subject || '',
          date_sent: email.scheduled_date || '',
          total_recipients: email.total_sent,
          unique_opens: email.unique_opens,
          open_rate: email.open_rate,
          total_opens: email.total_opened,
          average_time_to_open: 0, // Not available in summary, will need separate query if needed
          segment_name: email.segment_name,
          total_clicks: includeSpamChecker ? email.click_count_ip_filtered : email.click_count_unfiltered,
          unique_clicks: includeSpamChecker ? email.unique_click_count_ip_filtered : email.unique_click_count_unfiltered,
          unique_contacts: email.total_sent, // Approximation - unique contacts is roughly total sent
          opens_by_day: email.opens_by_day || [], // Use daily data from V2 endpoint
          sends_by_day: email.sends_by_day || [], // Use daily data from V2 endpoint
          clicks_by_day: email.clicks_by_day || []  // Use daily data from V2 endpoint
        }));
        
        
        setData(transformedData);
      } catch (error) {
        console.error('Error fetching email performance data:', error);
        setError('Failed to load email performance data. Please try again.');
        setData([]);
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchData();
  }, [dateRange, effectiveClientId, groupId, includeSpamChecker, isGroupMode]);

  // Clear click data cache when spam filter changes
  useEffect(() => {
    setClickDataMap(new Map());
  }, [includeSpamChecker]);

  const handleToggleClickDetails = async (emailId: number) => {
    // Disable click details in group mode (no contact-level data)
    if (isGroupMode) {
      return;
    }
    
    const newExpandedEmails = new Set(expandedEmails);
    
    if (newExpandedEmails.has(emailId)) {
      // Collapse
      newExpandedEmails.delete(emailId);
      setExpandedEmails(newExpandedEmails);
    } else {
      // Expand - fetch click data if not already loaded
      newExpandedEmails.add(emailId);
      setExpandedEmails(newExpandedEmails);
      
      if (!clickDataMap.has(emailId) && !loadingClickData.has(emailId)) {
        setLoadingClickData(prev => new Set(prev).add(emailId));
        
        try {
          const clickData = await fetchEmailClickData(effectiveClientId!, emailId, groupId, !includeSpamChecker);
          setClickDataMap(prev => new Map(prev).set(emailId, clickData));
        } catch (error) {
          console.error(`Error fetching click data for email ${emailId}:`, error);
          // Set an empty click data object to prevent showing placeholder text
          setClickDataMap(prev => new Map(prev).set(emailId, {
            email_id: emailId,
            email_name: '',
            email_subject: '',
            email_segment: null,
            email_scheduled_date: null,
            emails_sent: 0,
            total_hits: 0,
            total_unique_hits: 0,
            click_rate: 0,
            unique_click_rate: 0,
            click_details: []
          }));
        } finally {
          setLoadingClickData(prev => {
            const newSet = new Set(prev);
            newSet.delete(emailId);
            return newSet;
          });
        }
      }
    }
  };

  const handleOpenContactModal = useCallback((emailId: number, emailName: string, metric: 'sent' | 'opens' | 'clicks' | 'unique_clicks') => {
    // Disable contact modal in group mode (no contact-level data)
    if (isGroupMode) {
      return;
    }
    setSelectedEmailForModal({ id: emailId, name: emailName });
    setSelectedMetricForModal(metric);
    setContactModalOpen(true);
  }, [isGroupMode]);

  const handleCloseContactModal = useCallback(() => {
    setContactModalOpen(false);
    setSelectedEmailForModal(null);
  }, []);

  // Map modal handlers
  const handleOpenMapModal = useCallback(async (emailId: number, emailName: string, metric: 'opens' | 'clicks') => {
    // Disable geographic mapping in group mode
    if (isGroupMode) {
      return;
    }
    setSelectedEmailForMap({ id: emailId, name: emailName });
    setSelectedMetricForMap(metric);
    setMapModalOpen(true);
    await fetchGeographicData(emailId, metric);
  }, [isGroupMode]);

  const fetchGeographicData = useCallback(async (emailId: number, metric: 'opens' | 'clicks') => {
    // Disable geographic data in group mode
    if (isGroupMode || !effectiveClientId) return;
    
    try {
      setMapLoading(true);
      setMapError(null);
      
      const response = await apiService.get(`/api/analytics/email-performance/${emailId}/contacts`, {
        params: {
          client_id: effectiveClientId,
          metric: metric,
          date_range: dateRange.date_range,
          start_date: dateRange.startDate?.toISOString(),
          end_date: dateRange.endDate?.toISOString(),
          include_spam: !includeSpamChecker,
          include_geographic: true,
          limit: 1000 // Get more data for geographic mapping
        }
      });
      
      const data = response.data as any;
      const contacts = data.contacts || [];
      
      // Group contacts by latitude/longitude coordinates
      const geographicData = new Map<string, any>();
      
      contacts.forEach((contact: any) => {
        if (contact.latitude && contact.longitude) {
          const key = `${contact.latitude},${contact.longitude}`;
          
          if (!geographicData.has(key)) {
            geographicData.set(key, {
              latitude: parseFloat(contact.latitude),
              longitude: parseFloat(contact.longitude),
              count: 0,
              contacts: []
            });
          }
          
          const locationData = geographicData.get(key);
          locationData.count += metric === 'opens' ? (contact.open_count || 1) : (contact.click_count || 1);
          locationData.contacts.push({
            id: contact.id,
            first_name: contact.first_name,
            last_name: contact.last_name,
            email_address: contact.email_address
          });
        }
      });
      
      setMapData(Array.from(geographicData.values()));
    } catch (err: any) {
      console.error('Geographic data fetch error:', err);
      setMapError(`Failed to load geographic data: ${err.response?.status || 'Unknown error'}`);
      setMapData([]);
    } finally {
      setMapLoading(false);
    }
  }, [effectiveClientId, dateRange, includeSpamChecker]);

  const handleCloseMapModal = useCallback(() => {
    setMapModalOpen(false);
    setSelectedEmailForMap(null);
  }, []);

  const getMetricDescription = () => {
    switch (selectedMetricForModal) {
      case 'sent': return 'contacts who were sent this email';
      case 'opens': return 'contacts who opened this email';
      case 'clicks': return 'contacts who clicked this email';
      case 'unique_clicks': return 'contacts who clicked this email';
      default: return 'contacts';
    }
  };

  // Handle opening email preview modal
  const handleOpenEmailPreview = async (email: EmailPerformanceData) => {
    try {
      // Fetch full email details including HTML content
      const response = await apiService.get<any>(`/api/emails/${email.email_id}`);
      if (response.data) {
        setPreviewEmail(response.data);
        setPreviewModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching email details:', error);
      // Fallback to basic email info if API call fails
      setPreviewEmail(email);
      setPreviewModalOpen(true);
    }
  };
  
  // Handle closing email preview modal
  const handleCloseEmailPreview = () => {
    setPreviewModalOpen(false);
    setPreviewEmail(null);
  };

  const prepareChartData = useCallback((email: EmailPerformanceData): { chartData: Array<{x: string, sends: number, opens: number, clicks: number}>, useBarChart: boolean } => {
    // Check if we have any data
    const hasOpensData = email.opens_by_day && email.opens_by_day.length > 0;
    const hasSendsData = email.sends_by_day && email.sends_by_day.length > 0;
    const hasClicksData = email.clicks_by_day && email.clicks_by_day.length > 0;
    
    if (!hasOpensData && !hasSendsData && !hasClicksData) {
      // Only log if this is unexpected (i.e., if we have other data for this email)
      if (email.total_recipients > 0 || email.total_opens > 0 || email.total_clicks > 0) {
        console.warn(`No chart data for email ${email.email_id} (${email.email_name}) despite having summary data`);
      }
      return { chartData: [], useBarChart: false };
    }
    
    // Use the full date range from the filter to show complete timeline
    if (!dateRange.startDate || !dateRange.endDate) return { chartData: [], useBarChart: false };

    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);

    // Validate that we have valid dates
    if (!startDate || !endDate || startDate > endDate) {
      return { chartData: [], useBarChart: false };
    }

    try {
      // Create maps of dates to values for quick lookup
      const sendsMap = new Map(
        (email.sends_by_day || []).map(day => [
          format(parseISO(day.date), 'yyyy-MM-dd'),
          day.sends
        ])
      );
      
      const opensMap = new Map(
        (email.opens_by_day || []).map(day => [
          format(parseISO(day.date), 'yyyy-MM-dd'),
          day.opens
        ])
      );
      
      const clicksMap = new Map(
        (email.clicks_by_day || []).map(day => [
          format(parseISO(day.date), 'yyyy-MM-dd'),
          day.clicks
        ])
      );
      
      const totalDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      
      let chartData: Array<{x: string, sends: number, opens: number, clicks: number}>;
      let useBarChart = false;
      
      if (totalDays > 365) {
        // For 12+ months, show monthly rollups with bar chart
        chartData = createMonthlyRollupsMulti(startDate, endDate, sendsMap, opensMap, clicksMap);
        useBarChart = true;
      } else if (totalDays > 90) {
        // For 90+ days, show weekly rollups with bar chart
        chartData = createWeeklyRollupsMulti(startDate, endDate, sendsMap, opensMap, clicksMap);
        useBarChart = true;
      } else if (totalDays > 31) {
        // For 32+ days, show weekly rollups with bar chart
        chartData = createWeeklyRollupsMulti(startDate, endDate, sendsMap, opensMap, clicksMap);
        useBarChart = true;
      } else {
        // For 31 days or less, show daily data with line chart
        chartData = createDailyDataMulti(startDate, endDate, sendsMap, opensMap, clicksMap);
        useBarChart = false;
      }
      
      return { chartData, useBarChart };
    } catch (error) {
      console.error('Error creating chart data:', error, { startDate, endDate, emailId: email.email_id });
      return { chartData: [], useBarChart: false };
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // Helper function to create monthly rollups for multiple data types
  const createMonthlyRollupsMulti = (startDate: Date, endDate: Date, sendsMap: Map<string, number>, opensMap: Map<string, number>, clicksMap: Map<string, number>) => {
    const months: { [key: string]: { sends: number, opens: number, clicks: number } } = {};
    
    const allDays = eachDayOfInterval({ start: startDate, end: endDate });
    
    allDays.forEach(date => {
      const dateKey = format(date, 'yyyy-MM-dd');
      const monthKey = format(date, 'MMM yyyy');
      
      if (!months[monthKey]) {
        months[monthKey] = { sends: 0, opens: 0, clicks: 0 };
      }
      
      months[monthKey].sends += sendsMap.get(dateKey) || 0;
      months[monthKey].opens += opensMap.get(dateKey) || 0;
      months[monthKey].clicks += clicksMap.get(dateKey) || 0;
    });
    
    return Object.entries(months).map(([month, data]) => ({
      x: month,
      sends: data.sends,
      opens: data.opens,
      clicks: data.clicks
    }));
  };

  // Helper function to create weekly rollups for multiple data types
  const createWeeklyRollupsMulti = (startDate: Date, endDate: Date, sendsMap: Map<string, number>, opensMap: Map<string, number>, clicksMap: Map<string, number>) => {
    const weeks: { [key: string]: { sends: number, opens: number, clicks: number } } = {};
    
    const allDays = eachDayOfInterval({ start: startDate, end: endDate });
    
    allDays.forEach(date => {
      const dateKey = format(date, 'yyyy-MM-dd');
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      const weekKey = format(weekStart, 'MMM d');
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = { sends: 0, opens: 0, clicks: 0 };
      }
      
      weeks[weekKey].sends += sendsMap.get(dateKey) || 0;
      weeks[weekKey].opens += opensMap.get(dateKey) || 0;
      weeks[weekKey].clicks += clicksMap.get(dateKey) || 0;
    });
    
    return Object.entries(weeks).map(([week, data]) => ({
      x: week,
      sends: data.sends,
      opens: data.opens,
      clicks: data.clicks
    }));
  };

  // Helper function to create daily data for multiple data types
  const createDailyDataMulti = (startDate: Date, endDate: Date, sendsMap: Map<string, number>, opensMap: Map<string, number>, clicksMap: Map<string, number>) => {
    const allDays = eachDayOfInterval({
      start: startDate,
      end: endDate
    });
    
    return allDays.map(date => {
      const dateKey = format(date, 'yyyy-MM-dd');
      return {
        x: format(date, 'MMM d'),
        sends: sendsMap.get(dateKey) || 0,
        opens: opensMap.get(dateKey) || 0,
        clicks: clicksMap.get(dateKey) || 0
      };
    });
  };



  const renderEmailCard = useCallback((email: EmailPerformanceData) => {
    return (
      <Grid item xs={12} key={email.email_id}>
        <Paper elevation={2} className="p-6">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box>
                <Box display="flex" alignItems="center" gap={1} className="mb-2">
                  {isGroupMode ? (
                    <Typography variant="h6">
                      {email.email_name}
                    </Typography>
                  ) : (
                    <Typography 
                      variant="h6"
                      component="button"
                      onClick={() => handleOpenEmailPreview(email)}
                      sx={{ 
                        color: 'primary.main',
                        textDecoration: 'none',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {email.email_name}
                    </Typography>
                  )}
                  {isGroupMode && email.client_names && email.client_names.length > 0 && (
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Participating Agencies:
                          </Typography>
                          {email.client_names.map((name, idx) => (
                            <Typography key={idx} variant="body2">
                              • {name}
                            </Typography>
                          ))}
                        </Box>
                      }
                      arrow
                      placement="right"
                    >
                      <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" className="mb-3">
                  {email.subject}
                </Typography>
                
                {/* Metrics Grid - 3 boxes in 1 row */}
                <Grid container spacing={2} className="mb-3">
                  {/* Sent */}
                  <Grid item xs={4}>
                    <MetricCard
                      title="Sent"
                      value={email.total_recipients || 0}
                      onValueClick={isGroupMode ? undefined : () => handleOpenContactModal(email.email_id, email.email_name, 'sent')}
                      className="h-32"
                    />
                  </Grid>
                  
                  {/* Opens */}
                  <Grid item xs={4}>
                    <MetricCard
                      title="Opens"
                      value={email.total_opens || 0}
                      subtitle={`${email.total_recipients ? ((email.total_opens || 0) / email.total_recipients * 100).toFixed(2) : '0'}%`}
                      onValueClick={isGroupMode ? undefined : () => handleOpenContactModal(email.email_id, email.email_name, 'opens')}
                      onMapClick={isGroupMode ? undefined : () => handleOpenMapModal(email.email_id, email.email_name, 'opens')}
                      contactCount={email.unique_opens || 0}
                      className="h-32"
                    />
                  </Grid>
                  
                  {/* Clicks */}
                  <Grid item xs={4}>
                    <MetricCard
                      title="Clicks"
                      value={email.total_clicks || 0}
                      subtitle={`${email.total_recipients && email.total_clicks ? ((email.total_clicks / email.total_recipients) * 100).toFixed(2) : '0'}%`}
                      ctorSubtitle={`${email.total_opens && email.total_clicks ? ((email.total_clicks / email.total_opens) * 100).toFixed(2) : '0'}% CTOR`}
                      onValueClick={isGroupMode ? undefined : () => handleOpenContactModal(email.email_id, email.email_name, 'clicks')}
                      onMapClick={isGroupMode ? undefined : () => handleOpenMapModal(email.email_id, email.email_name, 'clicks')}
                      contactCount={email.unique_clicks || 0}
                      className="h-32"
                    />
                  </Grid>

                </Grid>
                
                {/* Segment Pills */}
                {email.segment_name && (
                  <Box display="flex" gap={2} alignItems="center" className="mb-3">
                    <Chip 
                      label={email.segment_name.charAt(0).toUpperCase() + email.segment_name.slice(1)}
                      size="medium"
                      sx={{
                        backgroundColor: getSegmentColor(email.segment_name),
                        color: 'white',
                        height: '24px',
                        minWidth: '80px',
                        '& .MuiChip-label': {
                          px: 1,
                          display: 'block',
                          whiteSpace: 'nowrap',
                          overflow: 'visible'
                        }
                      }}
                    />
                  </Box>
                )}

                {/* Additional Info */}
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="body1">
                    <strong>{new Date(email.date_sent).getTime() > Date.now() ? 'Scheduled:' : 'Sent:'}</strong> {email.date_sent ? new Date(email.date_sent).toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'Not scheduled'}
                  </Typography>
                </Box>

                {/* Click Details Toggle - Hidden in group mode */}
                {!isGroupMode && (
                  <Box className="mt-3">
                    <Box 
                      display="flex" 
                      alignItems="center"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleToggleClickDetails(email.email_id)}
                    >
                      <Typography variant="body2" color="primary" sx={{ mr: 1 }}>
                        {expandedEmails.has(email.email_id) ? 'Hide' : 'Show'} click details
                      </Typography>
                      <IconButton size="small">
                        {expandedEmails.has(email.email_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box height={200}>
                {(() => {
                  const { chartData, useBarChart } = prepareChartData(email);
                  const totalDays = Math.floor((new Date(dateRange.endDate!).getTime() - new Date(dateRange.startDate!).getTime()) / (1000 * 60 * 60 * 24)) + 1;
                  
                  // Check if any lines are visible
                  const hasVisibleLines = showSends || showOpens || showClicks;
                  
                  return chartData.length === 0 ? (
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center" 
                      height="100%"
                      color="text.secondary"
                    >
                      No performance data available for the selected date range
                    </Box>
                  ) : !hasVisibleLines ? (
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center" 
                      height="100%"
                      color="text.secondary"
                    >
                      Please enable at least one line type to view the chart
                    </Box>
                  ) : useBarChart ? (
                    // Bar chart for longer time periods
                    <ResponsiveBar
                      data={chartData}
                      keys={[
                        ...(showSends ? ['sends'] : []),
                        ...(showOpens ? ['opens'] : []),
                        ...(showClicks ? ['clicks'] : [])
                      ]}
                      indexBy="x"
                      margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
                      padding={0.3}
                      colors={[
                        ...(showSends ? ['#8884d8'] : []),
                        ...(showOpens ? ['#82ca9d'] : []),
                        ...(showClicks ? ['#ffc658'] : [])
                      ]}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -45,
                        legend: 'Date',
                        legendOffset: 50,
                        legendPosition: 'middle'
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Count',
                        legendOffset: -45,
                        legendPosition: 'middle'
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      labelTextColor="#374151"
                      legends={[
                        {
                          dataFrom: 'keys',
                          anchor: 'bottom-right',
                          direction: 'column',
                          justify: false,
                          translateX: 120,
                          translateY: 0,
                          itemsSpacing: 2,
                          itemWidth: 100,
                          itemHeight: 20,
                          itemDirection: 'left-to-right',
                          itemOpacity: 0.85,
                          symbolSize: 20,
                          effects: [
                            {
                              on: 'hover',
                              style: {
                                itemOpacity: 1
                              }
                            }
                          ]
                        }
                      ]}
                      tooltip={({ id, value, indexValue }) => (
                        <Box
                          sx={{
                            background: 'white',
                            padding: '8px 12px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            {indexValue}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {id}: {value?.toLocaleString() || 0}
                          </Typography>
                        </Box>
                      )}
                    />
                  ) : (
                    // Line chart for shorter time periods
                    <ResponsiveLine
                      data={[
                        ...(showSends ? [{
                          id: 'sends',
                          color: '#8884d8',
                          data: chartData.map(d => ({ x: d.x, y: d.sends }))
                        }] : []),
                        ...(showOpens ? [{
                          id: 'opens',
                          color: '#82ca9d',
                          data: chartData.map(d => ({ x: d.x, y: d.opens }))
                        }] : []),
                        ...(showClicks ? [{
                          id: 'clicks',
                          color: '#ffc658',
                          data: chartData.map(d => ({ x: d.x, y: d.clicks }))
                        }] : [])
                      ]}
                      margin={{ top: 20, right: 20, bottom: 60, left: 60 }}
                      xScale={{ type: 'point' }}
                      yScale={{
                        type: 'linear',
                        min: 0,
                        max: 'auto',
                        stacked: false,
                        reverse: false
                      }}
                      curve="monotoneX"
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 8,
                        tickRotation: totalDays > 15 ? -60 : -45,
                        legend: 'Date',
                        legendOffset: 50,
                        legendPosition: 'middle'
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 8,
                        tickRotation: 0,
                        legend: 'Count',
                        legendOffset: -45,
                        legendPosition: 'middle'
                      }}
                      enableGridX={false}
                      colors={[
                        ...(showSends ? ['#8884d8'] : []),
                        ...(showOpens ? ['#82ca9d'] : []),
                        ...(showClicks ? ['#ffc658'] : [])
                      ]}
                      pointSize={6}
                      pointColor={{ theme: 'background' }}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: 'serieColor' }}
                      enableArea={true}
                      areaOpacity={0.15}
                      useMesh={true}
                      animate={false}
                      legends={[
                        {
                          anchor: 'bottom-right',
                          direction: 'column',
                          justify: false,
                          translateX: 120,
                          translateY: 0,
                          itemsSpacing: 2,
                          itemWidth: 100,
                          itemHeight: 20,
                          itemDirection: 'left-to-right',
                          itemOpacity: 0.85,
                          symbolSize: 20,
                          effects: [
                            {
                              on: 'hover',
                              style: {
                                itemOpacity: 1
                              }
                            }
                          ]
                        }
                      ]}
                      tooltip={({ point }) => (
                        <Box
                          sx={{
                            background: 'white',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {point.data.x}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {point.seriesId}: <strong>{point.data.y}</strong>
                          </Typography>
                        </Box>
                      )}
                    />
                  );
                })()}
              </Box>
            </Grid>
          </Grid>

          {/* Click Details Panel - Full Width (Hidden in group mode) */}
          {!isGroupMode && (
            <Collapse in={expandedEmails.has(email.email_id)}>
            <Box className="mt-3 p-3" sx={{ backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              {loadingClickData.has(email.email_id) ? (
                <Typography variant="body2" color="text.secondary">
                  Loading click details...
                </Typography>
              ) : clickDataMap.has(email.email_id) ? (
                <Box>
                  <Typography variant="subtitle2" className="mb-2">
                    Click Details
                  </Typography>
                  {clickDataMap.get(email.email_id)?.click_details?.map((click, index) => (
                    <Box key={click.id || index} className="mb-2 p-2" sx={{ backgroundColor: 'white', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" className="mb-1">
                        <strong>URL:</strong>{' '}
                        <a 
                          href={click.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            color: '#1976d2', 
                            textDecoration: 'underline',
                            wordBreak: 'break-all'
                          }}
                        >
                          {click.url}
                        </a>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Clicks:</strong> {click.hits?.toLocaleString() || 0} Total, {click.unique_hits?.toLocaleString() || 0} Unique
                      </Typography>
                    </Box>
                  )) || []}
                  {(!clickDataMap.get(email.email_id)?.click_details || clickDataMap.get(email.email_id)?.click_details.length === 0) && (
                    <Typography variant="body2" color="text.secondary">
                      No click data available for this email.
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Click "Show click details" to load click information.
                </Typography>
              )}
            </Box>
          </Collapse>
          )}

        </Paper>
      </Grid>
    );
  }, [dateRange, showSends, showOpens, showClicks, expandedEmails, clickDataMap, loadingClickData, prepareChartData]);

  return (
    <Paper elevation={2} className="p-6">
      <Box display="flex" justifyContent="space-between" alignItems="center" className="mb-6">
        <Box display="flex" alignItems="center" gap={3}>
          {/* Line Visibility Toggles */}
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
              Show lines:
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showSends}
                  onChange={(e) => setShowSends(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Sent"
              sx={{ 
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  color: 'text.secondary'
                }
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showOpens}
                  onChange={(e) => setShowOpens(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Opens"
              sx={{ 
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  color: 'text.secondary'
                }
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showClicks}
                  onChange={(e) => setShowClicks(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label="Clicks"
              sx={{ 
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  color: 'text.secondary'
                }
              }}
            />
          </Box>
          
          {/* Spam Filter Toggle */}
          {onSpamCheckerChange && (
            <Tooltip 
              title={
                <Box>
                  <Typography color="white" variant="body2">
                    <strong>Filter out likely spam checker clicks</strong>
                  </Typography>
                  <Typography color="rgba(255, 255, 255, 0.8)" variant="body2" sx={{ mt: 1 }}>
                    When enabled, excludes opens and clicks from IP addresses flagged as spam checkers. 
                    This provides more accurate engagement metrics by filtering out automated bot activity.
                  </Typography>
                </Box>
              }
              arrow
              placement="top"
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.95)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={includeSpamChecker}
                    onChange={(e) => onSpamCheckerChange?.(e.target.checked)}
                    color="primary"
                  />
                }
                label="Filter out likely spam checker clicks"
                sx={{ 
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    color: 'text.secondary'
                  }
                }}
              />
            </Tooltip>
          )}
          
        </Box>
        <DateRangePicker
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
        />
      </Box>

      {error && (
        <Alert severity="error" className="mb-4">
          {error}
        </Alert>
      )}

      {loading ? (
        <LoadingState message="Loading email performance data..." />
      ) : data.length === 0 ? (
        <Box className="text-center py-8">
          <Typography variant="body1" color="text.secondary">
            No email performance data available for the selected date range.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {[...data].sort((a, b) => new Date(b.date_sent).getTime() - new Date(a.date_sent).getTime()).map(email => renderEmailCard(email))}
        </Grid>
      )}

      {/* Email Preview Modal */}
      <EmailPreviewModal
        open={previewModalOpen}
        onClose={handleCloseEmailPreview}
        email={previewEmail ? {
          name: (previewEmail as any).name || previewEmail.email_name,
          subject: previewEmail.subject,
          html: (previewEmail as any).html || '',
          scheduled_date: (previewEmail as any).scheduled_date || previewEmail.date_sent,
          agency_name: '',
          segment_name: previewEmail.segment_name,
          existing_tags: []
        } : null}
      />

      {/* Contact List Modal */}
      {selectedEmailForModal && (
        <ContactListModal
          key={`email-performance-contact-modal-${effectiveClientId}-${selectedEmailForModal.id}-${selectedMetricForModal}`}
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          title="Contacts"
          subtitle={`${getMetricDescription()} for ${selectedEmailForModal.name}${includeSpamChecker ? ' (excluding likely spam checker clicks)' : ' (including likely spam checker clicks)'}`}
          dataSource="email_performance"
          initialIncludeSpamChecker={includeSpamChecker}
          config={{
            emailId: selectedEmailForModal.id,
            emailName: selectedEmailForModal.name,
            metric: selectedMetricForModal,
            clientId: effectiveClientId!,
            dateRange: dateRange
          }}
        />
      )}

      {/* Geographic Map Modal */}
      {selectedEmailForMap && (
        <GeographicMapModal
          key={`email-performance-map-modal-${effectiveClientId}-${selectedEmailForMap.id}-${selectedMetricForMap}`}
          open={mapModalOpen}
          onClose={handleCloseMapModal}
          title="Geographic Distribution"
          subtitle={`${selectedMetricForMap === 'opens' ? 'Email opens' : 'Email clicks'} for ${selectedEmailForMap.name}${includeSpamChecker ? ' (excluding likely spam checker clicks)' : ' (including likely spam checker clicks)'}`}
          clientId={effectiveClientId!}
          mapData={mapData}
          loading={mapLoading}
          error={mapError}
        />
      )}
    </Paper>
  );
} 